import { initContract } from '@ts-rest/core'
import { z } from 'zod'

const c = initContract()
const prefix = '/v2/intersolve-classic'

// A single Intersolve Classic attempt that is eligible for admin cancellation.
// BigInt columns (refPos, value, transactionId) are serialised as strings —
// JSON cannot carry BigInt. Deliberately strict (no .passthrough()): this is a
// sensitive payload, so an accidentally-added field (e.g. a raw card number)
// must not slip through.
const CancellableAttemptSchema = z.object({
  attemptId: z.string(),
  status: z.string(),
  refPos: z.string(),
  cardIdLast4: z.string().nullable(),
  intersolveTransactionId: z.string().nullable(),
  value: z.string(),
  currency: z.string(),
  ean: z.string(),
  customerId: z.string(),
  integrationName: z.string(),
  incentiveName: z.string().nullable(),
  campaignName: z.string().nullable(),
  isTest: z.boolean().nullable(),
  createdAt: z.union([z.string(), z.date()]),
})

const CancelOutcomeSchema = z.object({
  attemptId: z.string(),
  refPos: z.string().nullable(),
  outcome: z.enum(['cancelled', 'would_cancel', 'failed', 'skipped']),
  via: z.enum(['transactionId', 'refPos']).nullable(),
  reason: z.string().nullable(),
})

const CancelResultSchema = z.object({
  dryRun: z.boolean(),
  requested: z.number(),
  cancelled: z.number(),
  wouldCancel: z.number(),
  failed: z.number(),
  skipped: z.number(),
  results: z.array(CancelOutcomeSchema),
})

export const intersolveClassic = c.router({
  listCancellable: {
    method: 'GET',
    path: `${prefix}/cancellable`,
    query: z.object({ customerId: z.string().optional() }),
    responses: {
      200: z.array(CancellableAttemptSchema),
      401: z.object({ message: z.string() }),
      500: z.object({ message: z.string() }),
    },
    summary:
      'List Intersolve Classic attempts eligible for admin cancellation (issued, cancel_pending)',
  },
  cancel: {
    method: 'POST',
    path: `${prefix}/cancel`,
    // dryRun defaults to true: an omitted flag never fires a real cancel.
    // attemptIds is capped: each id can trigger a real financial void, so an
    // unbounded array would let one request fan out into unlimited cancels.
    body: z.object({
      attemptIds: z.array(z.string()).min(1).max(500),
      dryRun: z.boolean().default(true),
    }),
    responses: {
      200: CancelResultSchema,
      400: z.object({ message: z.string() }),
      401: z.object({ message: z.string() }),
      500: z.object({ message: z.string() }),
    },
    summary:
      'Cancel Intersolve Classic transactions by attempt id (dryRun defaults to true)',
  },
})
